import React from "react";

import {
  FaCheckCircle,
  FaUsers,
} from "react-icons/fa";

import {
  TOURNAMENT_AUDIENCES,
  TOURNAMENT_BENEFITS,
  TOURNAMENT_FINAL_ARGUMENTS,
} from "../data/tournamentAboutData";

import useInViewOnce from "../hooks/useInViewOnce";

function AudienceCard({
  audience,
}) {
  const Icon = audience.icon;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
      <div
        className={`h-2 bg-gradient-to-r ${audience.accent}`}
      />

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div
          className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${audience.accent} text-xl text-white shadow-lg`}
        >
          <Icon />
        </div>

        <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          {audience.eyebrow}
        </p>

        <h3 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
          {audience.title}
        </h3>

        <ul className="mt-5 flex-1 space-y-3">
          {audience.items.map(
            (item) => (
              <li
                key={item}
                className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
              >
                <FaCheckCircle className="mt-1 shrink-0 text-emerald-500" />

                <span>{item}</span>
              </li>
            ),
          )}
        </ul>
      </div>
    </article>
  );
}

function BenefitCard({
  icon: Icon,
  value,
  label,
}) {
  return (
    <article className="rounded-[24px] border border-slate-200 bg-white p-5 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-400/20">
        <Icon />
      </div>

      <p className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
        {label}
      </p>
    </article>
  );
}

export default function TournamentAudienceSection({
  onDemoClick,
}) {
  const {
    ref,
    isVisible,
  } = useInViewOnce();

  return (
    <section
      ref={ref}
      className={`mt-16 transition-all duration-700 sm:mt-20 ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-8 opacity-0"
      }`}
    >
      <div className="max-w-4xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-300">
          <FaUsers />
          Pour chaque participant
        </div>

        <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl dark:text-white">
          Les bons outils pour chaque acteur de la compétition
        </h2>

        <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
          L’organisateur, les clubs, les tables d’arbitrage et le public n’utilisent pas les mêmes écrans. MonAppliTournoi adapte les informations et les fonctionnalités à chaque usage.
        </p>
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {TOURNAMENT_AUDIENCES.map(
          (audience) => (
            <AudienceCard
              key={audience.key}
              audience={audience}
            />
          ),
        )}
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {TOURNAMENT_BENEFITS.map(
          (benefit) => (
            <BenefitCard
              key={benefit.value}
              {...benefit}
            />
          ),
        )}
      </div>

      <div className="relative mt-10 overflow-hidden rounded-[38px] bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 p-6 text-white shadow-2xl shadow-slate-950/20 sm:p-8 lg:p-10">
        <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

        <div className="relative grid items-center gap-8 lg:grid-cols-[1fr_420px]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-300">
              Une organisation plus fluide
            </p>

            <h3 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Réduisez les manipulations et facilitez la circulation des informations
            </h3>

            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-300">
              MonAppliTournoi évite de multiplier les fichiers, feuilles papier et tableaux indépendants. Les données sont centralisées et disponibles au bon moment pour chaque utilisateur.
            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {TOURNAMENT_FINAL_ARGUMENTS.map(
                ({ icon: Icon, text }) => (
                  <div
                    key={text}
                    className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <Icon className="mt-1 shrink-0 text-emerald-400" />

                    <span className="text-sm leading-6 text-slate-200">
                      {text}
                    </span>
                  </div>
                ),
              )}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onDemoClick}
                className="inline-flex min-h-12 items-center justify-center rounded-full bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:bg-orange-600"
              >
                Voir MonAppliTournoi en ligne
              </button>

              <a
                href="#tournoi-contact"
                className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/15 bg-white/10 px-6 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-white/20"
              >
                Demander une démonstration
              </a>
            </div>
          </div>

          <div className="overflow-hidden rounded-[30px] border border-white/10 bg-white/10 p-3 backdrop-blur">
            <img
              src="/images/MACTournoi/10.png"
              alt="Résultats publics de MonAppliTournoi"
              className="aspect-[4/3] w-full rounded-[22px] object-contain bg-slate-900"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}